import { ApiEndpoints } from "./apiEndpoints";
import { baseApi } from "./apiHelpers";
import { StreamEvent } from "./stream.type";

export type StreamConnection = {
  close: () => void;
};

const getSupabaseSessionTokens = () => {
  try {
    const supabaseId = process.env.REACT_APP_SUPABASE_PROJECT_ID;
    if (!supabaseId || typeof localStorage === "undefined") {
      return null;
    }

    const rawSession = localStorage.getItem(`sb-${supabaseId}-auth-token`);
    if (!rawSession) {
      return null;
    }

    const parsed = JSON.parse(rawSession);
    return {
      access_token: parsed?.access_token as string | undefined,
      refresh_token: parsed?.refresh_token as string | undefined,
    };
  } catch (error) {
    console.warn("stream_session_parse_failed", error);
    return null;
  }
};

const getStreamHeaders = (): Record<string, string> => {
  const session = getSupabaseSessionTokens();

  return {
    "x-client-tz": Intl?.DateTimeFormat?.().resolvedOptions?.().timeZone || "UTC",
    ...(session?.access_token ? { access_token: session.access_token } : {}),
    ...(session?.refresh_token ? { refresh_token: session.refresh_token } : {}),
  };
};

const normalizeStreamPath = (messageId: string) =>
  `${ApiEndpoints.stream}/medical/${encodeURIComponent(messageId)}`;

const buildStreamUrl = (messageId: string) => {
  const path = normalizeStreamPath(messageId);
  const query = new URLSearchParams(getStreamHeaders());
  const qs = query.toString();
  return qs ? `${baseApi}${path}?${qs}` : `${baseApi}${path}`;
};

export async function streamMessageEvents({
  messageId,
  onEvent,
  onError,
  onEnd,
}: {
  messageId: string;
  onEvent?: (data: StreamEvent) => void;
  onError?: (error: any) => void;
  onEnd?: () => void;
}): Promise<StreamConnection> {
  try {
    const eventSource = new EventSource(buildStreamUrl(messageId));

    eventSource.addEventListener("message", function (event: MessageEvent) {
      if (!event.data) {
        return;
      }

      try {
        const data: StreamEvent = JSON.parse(event.data);
        onEvent?.(data);

        if (data?.finalResponse) {
          onEnd?.();
          eventSource.close();
        }
      } catch (error) {
        onError?.(error);
      }
    });

    eventSource.addEventListener("error", function (error) {
      onError?.(error);
      onEnd?.();
      eventSource.close();
    });

    return eventSource;
  } catch (error) {
    console.error("Error streaming message events:", error);
    throw error;
  }
}
